import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { IconButton, Stack } from '@mui/material';
import { GridRowId } from '@mui/x-data-grid-premium';

import dayjs from 'dayjs';

import { ShipmentResponse } from '@/api/data-contracts';
import { ReactComponent as EyeIcon } from '@/assets/icons/eye-outline-icon.svg';
import { GeneratikTableColumn } from '@/shared/ui/GeneratikTable';
import { SortOption } from '@/shared/ui/GeneratikTable/GeneratikTableToolbar.types';
import { getStatusChipColumnWidth } from '@/shared/ui/StatusChip';

import { getAwbListStatus, getShipmentLastUpdate } from '../../../model/allAwbs';
import AwbStatusChip from '../../AwbStatusChip';

interface UseAllAwbsColumnsParams {
  senderNameByClientId: Record<string, string>;
  expandedIds: GridRowId[];
  onToggle: (id: string) => void;
  onView: (shipment: ShipmentResponse) => void;
  statusLabels: string[];
}

const STATUS_MIN_WIDTH_PX = 120;

const ICON_BUTTON_SX = { p: 0, width: '1.375rem', height: '1.375rem', color: 'grey.600' };

const useAllAwbsColumns = ({
  senderNameByClientId,
  expandedIds,
  onToggle,
  onView,
  statusLabels,
}: UseAllAwbsColumnsParams) => {
  const { t } = useTranslation();
  const statusWidth = useMemo(
    () => getStatusChipColumnWidth(statusLabels, STATUS_MIN_WIDTH_PX),
    [statusLabels],
  );

  const columns = useMemo<GeneratikTableColumn<ShipmentResponse>[]>(
    () => [
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        disableColumnMenu: true,
        disableReorder: true,
        width: 96,
        minWidth: 96,
        renderCell: ({ row }) => {
          const expanded = expandedIds.includes(row.id);
          return (
            <Stack
              direction="row"
              alignItems="center"
              gap="0.75rem"
              onClick={(event) => event.stopPropagation()}
            >
              <IconButton
                sx={ICON_BUTTON_SX}
                onClick={() => onToggle(row.id)}
                aria-expanded={expanded}
                data-testid="all-awbs-expand"
              >
                <KeyboardArrowDownRoundedIcon
                  sx={{
                    fontSize: '1.375rem',
                    transform: expanded ? 'none' : 'rotate(-90deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </IconButton>
              <IconButton
                sx={ICON_BUTTON_SX}
                onClick={() => onView(row)}
                data-testid="all-awbs-view"
              >
                <EyeIcon />
              </IconButton>
            </Stack>
          );
        },
      },
      {
        field: 'awbNumber',
        headerName: t('awb.column_awb_number'),
        sortable: true,
        cellType: 'shortText',
        valueGetter: (_value, row) => row.awbNumber ?? t('awb.pending_number'),
      },
      {
        field: 'recipient',
        headerName: t('Recipient'),
        sortable: true,
        cellType: 'text',
        valueGetter: (_value, row) => row.recipient?.name ?? '-',
      },
      {
        field: 'city',
        headerName: t('awb.column_city'),
        sortable: true,
        cellType: 'shortText',
        valueGetter: (_value, row) => row.recipient?.locality ?? '-',
      },
      {
        field: 'pickupLocation',
        headerName: t('awb.column_pickup_location'),
        sortable: true,
        cellType: 'text',
        valueGetter: (_value, row) => senderNameByClientId[row.clientId] ?? row.clientId,
      },
      {
        field: 'status',
        headerName: t('Status'),
        sortable: true,
        cellType: 'status',
        width: statusWidth,
        minWidth: statusWidth,
        valueGetter: (_value, row) => t(getAwbListStatus(row).labelKey),
        renderCell: ({ row }) => <AwbStatusChip shipment={row} testId="all-awbs-status" />,
      },
      {
        field: 'lastUpdate',
        headerName: t('awb.column_last_update'),
        sortable: true,
        cellType: 'shortText',
        valueGetter: (_value, row) => getShipmentLastUpdate(row),
        renderCell: ({ value }) => (value ? dayjs(String(value)).format('DD MMM, HH:mm') : '-'),
      },
    ],
    [expandedIds, onToggle, onView, senderNameByClientId, statusWidth, t],
  );

  const sortOptions = useMemo<SortOption[]>(
    () =>
      columns
        .filter((column) => column.sortable)
        .map((column) => ({ field: column.field, label: column.headerName ?? '' })),
    [columns],
  );

  return { columns, sortOptions };
};

export default useAllAwbsColumns;
